import { Notification } from 'element-ui'

// WebSocket 单例管理器
const WS_STORAGE_KEY = 'websocket_userId'
const WS_HEARTBEAT_KEY = 'websocket_heartbeat'
const MAX_RECONNECT_ATTEMPTS = 10
const INITIAL_RECONNECT_DELAY = 3000 // 3秒
const KICKED_RETRY_INTERVAL = 10000 // 被踢后每10秒尝试恢复一次
const HEARTBEAT_INTERVAL = 5000 // 心跳间隔5秒

let websocket = null
let reconnectAttempts = 0
let reconnectTimer = null
let heartbeatTimer = null
let kickedRetryTimer = null
let wsUrl = ''
let userId = ''
let isManualClose = false

/**
 * 申请浏览器桌面通知权限
 */
function checkNotificationPermission(callback) {
  if (!('Notification' in window)) {
    callback(false)
    return
  }
  if (Notification.permission === 'granted') {
    callback(true)
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then(permission => {
      callback(permission === 'granted')
    })
  } else {
    callback(false)
  }
}

/**
 * 桌面通知
 */
function showDesktopNotification(title, message) {
  checkNotificationPermission(function(granted) {
    if (granted) {
      new Notification(title, { body: message })
    }
  })
}

/**
 * 根据消息内容判断通知标题
 */
function getNotificationTitle(message) {
  if (message.indexOf('预配') !== -1) return '已完成预配通知'
  if (message.indexOf('审核') !== -1) return '提交审核通知'
  if (message.indexOf('已提交库内理货') !== -1) return '已提交库内理货通知'
  if (message.indexOf('未反馈的询价') !== -1) return '未反馈的询价通知'
  if (message.indexOf('未读的报价沟通通知') !== -1) return '未读的报价沟通通知'
  if (message.indexOf('未读的询价沟通通知') !== -1) return '未读的询价沟通通知'
  return '系统通知'
}

/**
 * 收到消息处理
 */
function handleMessage(event) {
  const message = event.data
  console.log('[WebSocket] 收到消息:', message)

  const title = getNotificationTitle(message)

  // Element UI 应用内通知
  Notification.info({
    title: title,
    message: message,
    duration: 8000,
    offset: 60
  })

  // 浏览器桌面通知
  showDesktopNotification(title, message)

  // 将消息转发给所有 iframe 子页面
  broadcastToIframes(message)
}

/**
 * 向所有 iframe 子页面广播消息
 */
function broadcastToIframes(message) {
  try {
    const iframes = document.querySelectorAll('iframe')
    iframes.forEach(iframe => {
      try {
        iframe.contentWindow.postMessage({
          type: 'websocket-message',
          data: message
        }, window.location.origin)
      } catch (e) {
        // 跨域 iframe 忽略
      }
    })
  } catch (e) {
    // ignore
  }
}

/**
 * 计算重连延迟（指数退避，最大30秒）
 */
function getReconnectDelay() {
  const delay = INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttempts)
  return Math.min(delay, 30000)
}

/**
 * 启动心跳：定时向 localStorage 写入时间戳，
 * 让同账号被顶替的标签页感知此连接存活
 */
function startHeartbeat() {
  stopHeartbeat()
  heartbeatTimer = setInterval(() => {
    try {
      localStorage.setItem(WS_HEARTBEAT_KEY + '_' + userId, String(Date.now()))
    } catch (e) {
      // localStorage 不可用时忽略
    }
  }, HEARTBEAT_INTERVAL)
}

/**
 * 停止心跳并清除标记
 */
function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
  if (userId) {
    try { localStorage.removeItem(WS_HEARTBEAT_KEY + '_' + userId) } catch (e) { /* ignore */ }
  }
}

/**
 * 启动被踢后的恢复重试：每 KICKED_RETRY_INTERVAL 尝试一次，
 * 检测到原活跃连接的心跳停止后重连成功
 */
function startKickedRetry() {
  stopKickedRetry()
  let retryCount = 0
  kickedRetryTimer = setInterval(() => {
    retryCount++
    // 检查是否有同账号的活跃心跳（另一个标签页是否还活着）
    let heartbeatAlive = false
    try {
      const lastBeat = localStorage.getItem(WS_HEARTBEAT_KEY + '_' + userId)
      if (lastBeat) {
        const elapsed = Date.now() - Number(lastBeat)
        // 心跳间隔5秒，超过12秒认为连接已断开
        heartbeatAlive = elapsed < 12000
      }
    } catch (e) { /* ignore */ }

    if (heartbeatAlive) {
      console.log('[WebSocket] 活跃连接仍存活，等待中... (第' + retryCount + '次检查)')
      return
    }

    // 心跳已停止，说明活跃标签页已断开，尝试重连
    console.log('[WebSocket] 活跃连接已断开，尝试恢复连接...')
    stopKickedRetry()
    reconnectAttempts = 0
    connect(wsUrl + '/wt/' + userId)
  }, KICKED_RETRY_INTERVAL)
}

/**
 * 停止被踢重试定时器
 */
function stopKickedRetry() {
  if (kickedRetryTimer) {
    clearInterval(kickedRetryTimer)
    kickedRetryTimer = null
  }
}

/**
 * 连接 WebSocket
 */
function connect(url) {
  if (websocket && (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING)) {
    console.log('[WebSocket] 已有活跃连接，跳过重复创建')
    return
  }

  if (!url) {
    console.error('[WebSocket] 连接地址为空')
    return
  }

  try {
    websocket = new WebSocket(url)
    console.log('[WebSocket] 正在连接:', url)

    websocket.onopen = function(event) {
      console.log('[WebSocket] 连接成功, sessionId:', wsUrl + '/wt/' + userId)
      reconnectAttempts = 0
      // 停止被踢重试定时器
      stopKickedRetry()
      // 启动心跳：定时写入localStorage，让同账号被踢的标签页知道此连接存活
      startHeartbeat()
    }

    websocket.onmessage = handleMessage

    websocket.onerror = function(error) {
      console.error('[WebSocket] 连接错误:', error)
    }

    websocket.onclose = function(event) {
      console.log('[WebSocket] 连接关闭, 状态码:', event.code, '原因:', event.reason)
      // 停止心跳
      stopHeartbeat()
      // 状态码4001：被同账号新标签页顶替，保留状态，定期尝试恢复连接
      if (event.code === 4001) {
        console.log('[WebSocket] 已被新标签页连接顶替，进入恢复等待模式')
        // 不清除 sessionStorage，不清除 wsUrl/userId
        // 启动定期重试：新标签页断开后，此标签页自动恢复连接
        startKickedRetry()
        return
      }
      if (!isManualClose && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const delay = getReconnectDelay()
        console.log(`[WebSocket] ${delay / 1000}秒后尝试第${reconnectAttempts + 1}次重连...`)
        reconnectTimer = setTimeout(() => {
          reconnectAttempts++
          if (userId && wsUrl) {
            connect(wsUrl + '/wt/' + userId)
          }
        }, delay)
      } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.log('[WebSocket] 已达到最大重连次数，停止重连')
      }
    }
  } catch (e) {
    console.error('[WebSocket] 创建连接失败:', e)
  }
}

/**
 * 关闭 WebSocket
 */
function close() {
  isManualClose = true
  reconnectAttempts = MAX_RECONNECT_ATTEMPTS // 阻止自动重连
  stopHeartbeat()
  stopKickedRetry()
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (websocket) {
    try {
      websocket.close()
    } catch (e) {
      // ignore
    }
    websocket = null
  }
  console.log('[WebSocket] 已手动关闭连接')
}

/**
 * 重置状态（用于重新连接）
 */
function resetState() {
  isManualClose = false
  reconnectAttempts = 0
  stopKickedRetry()
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

/**
 * 初始化 WebSocket 连接（由 Layout 组件调用）
 * @param {string} baseWsUrl - WebSocket 基础地址，如 ws://localhost:8081
 * @param {string} uid - 用户ID
 */
export function initWebSocket(baseWsUrl, uid) {
  if (!baseWsUrl || !uid) {
    console.warn('[WebSocket] 缺少 wsUrl 或 userId，无法连接', { baseWsUrl, uid })
    return
  }

  wsUrl = baseWsUrl
  userId = uid
  resetState()

  // 保存 userId 到 sessionStorage，页面刷新时恢复
  sessionStorage.setItem(WS_STORAGE_KEY, JSON.stringify({ wsUrl: baseWsUrl, userId: uid }))

  const fullUrl = baseWsUrl + '/wt/' + uid
  connect(fullUrl)
}

/**
 * 从 sessionStorage 恢复 WebSocket 连接（页面刷新后Layout挂载时调用）
 */
export function resumeWebSocket() {
  try {
    const stored = sessionStorage.getItem(WS_STORAGE_KEY)
    if (stored) {
      const { wsUrl: storedWsUrl, userId: storedUserId } = JSON.parse(stored)
      if (storedWsUrl && storedUserId) {
        initWebSocket(storedWsUrl, storedUserId)
        return true
      }
    }
  } catch (e) {
    // ignore
  }
  return false
}

/**
 * 关闭 WebSocket 并清除存储数据
 */
export function closeWebSocket() {
  close()
  sessionStorage.removeItem(WS_STORAGE_KEY)
  wsUrl = ''
  userId = ''
}

/**
 * 检查 WebSocket 是否已连接
 */
export function isWebSocketConnected() {
  return websocket && websocket.readyState === WebSocket.OPEN
}

// 页面关闭时自动清理
window.addEventListener('beforeunload', () => {
  stopHeartbeat()
  stopKickedRetry()
  close()
})
